<template>
    <div class="user-groups-page">
        <vrm-header>
            <template v-slot:header-controls>
                <div class="vrm-tasks">
                    <div class="vrm-tasks__task">
                        <vrm-toggle-button
                            v-model="search.active"
                            icon-class="sg-vrm-icon_040_search-16px"
                        />
                    </div>
                    <div class="vrm-tasks__task visible-lg">
                        <button class="sg-btn sg-blue" @click.prevent="addTeam">
                            {{ $t('teams.add_team', { i18nFallback: 'Create team' }) }}
                        </button>
                    </div>
                </div>
            </template>
        </vrm-header>
        <vrm-title>
            <template v-slot:page-title>
                <h1 class="vrm-page-title">
                    <span>{{ $t('teams.teams', { i18nFallback: 'Teams' }) }}</span>
                </h1>
            </template>
            <template v-slot:header-panels>
                <vrm-header-search-panel
                    key="search"
                    v-model="search.query"
                    :open="search.active"
                />
            </template>
            <template v-slot:header-controls>
                <div class="vrm-tasks">
                    <div class="vrm-tasks__task">
                        <vrm-toggle-button
                            v-model="search.active"
                            icon-class="sg-vrm-icon_040_search-16px"
                        />
                    </div>
                    <div class="vrm-tasks__task visible-lg">
                        <button class="sg-btn sg-blue" @click.prevent="addTeam">
                            {{ $t('teams.add_team', { i18nFallback: 'Create team' }) }}
                        </button>
                    </div>
                </div>
            </template>
        </vrm-title>
        <div class="container">
            <p>{{ $t('teams.team_explainer') }}</p>
            <div class="sg-btn__container sg-btn__container--align-right hidden-lg">
                <button class="sg-btn sg-blue" @click.prevent="addTeam">
                    {{ $t('teams.add_team', { i18nFallback: 'Create team' }) }}
                </button>
            </div>
            <p v-if="loading" class="sg-color-blue">
                {{ $t('core.loading', { i18nFallback: 'Loading...' }) }}
            </p>
            <vrm-scroll-fade>
                <vrm-foldable-table
                    :items="orderedTeams"
                    key-by="idUserGroup"
                    :on-click-row="viewTeam"
                >
                    <template v-slot:thead>
                        <tr>
                            <th>{{ $t('teams.teams', { i18nFallback: 'Teams' }) }}</th>
                            <th>{{ $t('teams.members', { i18nFallback: 'Members' }) }}</th>
                            <th>
                                {{
                                    $t('teams.linked_installations', {
                                        i18nFallback: 'Linked installations',
                                    })
                                }}
                            </th>
                            <th />
                        </tr>
                    </template>
                    <template v-slot:main="{ item: team }">
                        <td>{{ team.name }}</td>
                        <td class="vrm-table__nowrap">
                            <vrm-avatar-stack :users="team.members" admin-key="isAdmin" />
                        </td>
                        <td>{{ team.siteCount }}</td>
                        <td class="vrm-table__actions">
                            <router-link
                                :to="{
                                    name: 'base.user-group',
                                    params: { id: team.idUserGroup },
                                }"
                                class="vrm-table__icon--link"
                            >
                                <i class="sg-vrm-icon_049_arrow-right-16px" />
                            </router-link>
                        </td>
                    </template>
                    <template v-slot:noEntries>
                        {{
                            $t('teams.no_teams_create_hint', {
                                i18nFallback:
                                    'You have not created any teams yet, first create a team to add users to it.',
                            })
                        }}
                    </template>
                </vrm-foldable-table>
            </vrm-scroll-fade>
        </div>
    </div>
</template>
<script>
import { Vue, Component } from 'vue-property-decorator';
import { AngularRoute, ngInject } from '@/vue/decorators/angular-interop';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import VrmAvatarStack from '@/vue/components/shared/VrmAvatarStack';
import VrmFoldableTable from '@/vue/components/molecules/VrmFoldableTable';
import VrmScrollFade from '@/vue/components/shared/VrmScrollFade';
import VrmHeader from '@/vue/components/layout/VrmHeader';
import VrmTitle from '@/vue/components/layout/VrmTitle';
import VrmToggleButton from '@/vue/components/shared/toggles/VrmToggleButton';
import VrmAvatar from '@/vue/components/shared/VrmAvatar';
import UserGroupCreateModal from '@/vue/components/modals/UserGroupCreateModal';
import VrmHeaderSearchPanel from '@/vue/components/molecules/VrmHeaderSearchPanel';
import VrmIconButton from '@/vue/components/molecules/VrmIconButton';

@Component({
    components: {
        VrmIconButton,
        VrmHeaderSearchPanel,
        VrmAvatar,
        VrmToggleButton,
        VrmHeader,
        VrmFoldableTable,
        VrmAvatarStack,
        VrmScrollFade,
        VrmTitle,
    },
})
@AngularRoute('base.user-groups', '/teams', { slot: 'main' })
@AngularLocaleBundle('core')
@AngularLocaleBundle('nav')
@AngularLocaleBundle('teams')
export default class UserGroups extends Vue {
    /** @type UserGroupsService */
    @ngInject()
    UserGroupsService;

    loading = false;
    search = {
        active: false,
        query: '',
    };

    get teams() {
        return this.$store.state.UserGroupsService.groups;
    }

    get orderedTeams() {
        return this.teams.slice().sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
    }

    async created() {
        try {
            this.loading = true;
            await Promise.all([this.loadUser(), this.loadGroups()]);
        } finally {
            this.loading = false;
        }
    }

    async loadUser() {
        this.localUser = await this.UserService.getUser();
    }

    async loadGroups() {
        return await this.UserGroupsService.loadUserGroups();
    }

    expanded = [];

    toggle(teamId) {
        const index = this.expanded.indexOf(teamId);
        if (index >= 0) {
            this.expanded.splice(index, 1);
        } else {
            this.expanded.push(teamId);
        }
    }

    isExpanded(teamId) {
        return this.expanded.includes(teamId);
    }

    @ngInject()
    VrmModalService;
    @ngInject()
    $rootScope;
    @ngInject()
    UserService;

    localUser = null;

    getGroupMembership(group) {
        if (!this.localUser) return null;
        const idUser = this.localUser.id;
        return group.members.find(membership => membership.idUser === idUser);
    }

    canMakeChanges(group) {
        if (!this.localUser) return false;
        return this.localUser.id === group.idOwner;
    }

    confirmDeletion(team) {
        this.VrmModalService.openConfirmModal(this.$rootScope, 'delete', confirmed => {
            if (confirmed) {
                this.UserGroupsService.deleteUserGroup(team.idUserGroup);
            }
        });
    }

    addTeam() {
        this.VrmModalService.openVueModal(UserGroupCreateModal);
    }

    viewTeam(idUserGroup) {
        this.$router.push({
            name: 'base.user-group',
            params: {
                id: idUserGroup,
            },
        });
    }
}
</script>
